import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const ApplicationDevelopment = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Application Development</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Build robust, scalable, and user-friendly applications that drive business growth and enhance user experience.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Development Services</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-mobile-alt text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Mobile Applications</h3>
                  <p className="text-gray-600">Native and cross-platform mobile apps for iOS and Android.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-globe text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Web Applications</h3>
                  <p className="text-gray-600">Modern, responsive web applications using latest technologies.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-cogs text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">API Development</h3>
                  <p className="text-gray-600">RESTful and GraphQL APIs for seamless integration.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-desktop text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Desktop Applications</h3>
                  <p className="text-gray-600">Cross-platform desktop applications for Windows, Mac, and Linux.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Technologies We Use</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-react text-4xl text-blue-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">React/Next.js</h3>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-node-js text-4xl text-green-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Node.js</h3>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-python text-4xl text-yellow-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Python/Django</h3>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-java text-4xl text-red-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Java/Spring</h3>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-swift text-4xl text-orange-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Swift/Kotlin</h3>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fas fa-database text-4xl text-purple-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Databases</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Development Approach */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Development Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-users text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Agile Methodology</h3>
              <p className="text-gray-600">Iterative development with continuous feedback and improvement.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-code text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Clean Code</h3>
              <p className="text-gray-600">Well-documented, maintainable code following best practices.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-shield-alt text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Security First</h3>
              <p className="text-gray-600">Built-in security measures and compliance standards.</p>
            </div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Frontend Excellence</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Responsive design for all devices
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Modern UI/UX with intuitive navigation
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Performance optimization
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Cross-browser compatibility
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-blue-600 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Backend Reliability</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Scalable architecture design
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Robust API development
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Database optimization
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3"></i>
                Real-time capabilities
              </li>
            </ul>
          </div>
        </div>

        {/* Development Process */}
        <div className="bg-gray-900 rounded-lg shadow-lg p-8 text-white mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Development Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Discovery</h3>
              <p className="text-sm opacity-90">Requirements gathering and analysis</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Design</h3>
              <p className="text-sm opacity-90">UI/UX design and architecture planning</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Development</h3>
              <p className="text-sm opacity-90">Agile development with regular updates</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Testing</h3>
              <p className="text-sm opacity-90">Comprehensive testing and QA</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">5</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Deployment</h3>
              <p className="text-sm opacity-90">Launch and ongoing support</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Build Your Next Application?</h2>
          <p className="text-xl text-gray-600 mb-8">Let's turn your ideas into powerful, scalable applications.</p>
          <button 
            onClick={handleContactClick}
            className="bg-primary hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors"
          >
            Start Your Project
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDevelopment;
